import { PropsWithChildren, ReactNode, useImperativeHandle } from 'react'

import { useControllableValue } from 'ahooks'
import { Modal, ModalProps } from 'antd'
import { createStyles } from 'antd-style'

import { pick } from '../../../utils/object'
import { useDraggableModalStore } from '../Store/hooks'

import { DraggableModalCoreProps } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  modal: {
    [`& .${prefixCls}-modal-container`]: {
      padding: 0,
      border: 0,
      boxShadow: 'none'
    },
    [`& .${prefixCls}-modal-close`]: {
      top: -token.paddingXS / 2,
      insetInlineEnd: -token.paddingXS
    },
    [`& .${prefixCls}-modal-header`]: {
      marginBlockEnd: token.marginSM
    }
  },
  dragArea: {
    paddingBlock: token.paddingMD,
    paddingInline: token.paddingLG,
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadow,
    pointerEvents: 'auto',
    outline: 'none',
    cursor: 'move'
  },
  contentArea: {
    cursor: 'default'
  }
}))

const DraggableHandler = ({ children }: PropsWithChildren) => {
  const { cx, styles, prefixCls } = useStyles()
  const { attributes, listeners, setNodeRef, coordinates, transform } =
    useDraggableModalStore(state =>
      pick(state, [
        'attributes',
        'listeners',
        'setNodeRef',
        'coordinates',
        'transform'
      ])
    )
  return (
    <div
      ref={setNodeRef}
      className={cx(styles.dragArea, `${prefixCls}-modal-drag-area`)}
      style={{
        transform,
        position: 'relative' as const,
        top: coordinates.y,
        left: coordinates.x
      }}
      {...listeners}
      {...attributes}
    >
      <div
        className={cx(styles.contentArea, `${prefixCls}-modal-content-area`)}
        role='button'
        tabIndex={0}
        onKeyDown={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

export const DraggableModalCore = ({
  /* -------------------------------------------------------------------------- */
  /*                                 Modal Props                                */
  /* -------------------------------------------------------------------------- */
  ref,
  className,
  title,
  closable = true,
  okText = '确定',
  cancelText = '取消',
  modalRender,
  onCancel,
  onOk,
  // useControllableValue 是根据 hasOwnProperty 来判断是否受控，所以 open 跟 onOpenChange 不能解构出来
  ...restProps
}: DraggableModalCoreProps) => {
  const { cx, styles } = useStyles()

  const customModalRender = (modal: ReactNode) => {
    const node = <DraggableHandler>{modal}</DraggableHandler>
    return modalRender ? modalRender(node) : node
  }

  const [modalOpen, setModalOpen] = useControllableValue(restProps, {
    defaultValue: false,
    valuePropName: 'open',
    trigger: 'onOpenChange'
  })

  const handleCancel: ModalProps['onCancel'] = event => {
    setModalOpen(false)
    onCancel?.(event)
  }

  const handleOk: ModalProps['onOk'] = async event => {
    const shouldClose = await onOk?.(event)
    if (shouldClose !== false) {
      setModalOpen(false)
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      open: () => setModalOpen(true),
      close: () => setModalOpen(false)
    }),
    [setModalOpen]
  )

  return (
    <Modal
      {...restProps}
      cancelText={cancelText}
      className={cx(styles.modal, className)}
      closable={closable && !!title}
      modalRender={customModalRender}
      okText={okText}
      open={modalOpen}
      title={title}
      onCancel={handleCancel}
      onOk={handleOk}
    />
  )
}
